/**
 * What the omnibar offers out of the box: the space's pages and your account's, settings (straight to the one meant),
 * actions, the space's entities, your other spaces, and items (searched as you type). Each its own source, so a
 * feature can add more beside them (`registerSource` in lib/omnibar.ts).
 */
import { api, logout } from "../api/client.ts";
import type { Bookmark, ItemRow, Me, ReportList, SpaceOut } from "../api/types.gen.ts";
import { english, type Key, t } from "../i18n/index.ts";
import { askHref, INVITE, NEW_DOCUMENT, NEW_ITEM, NEW_SPREADSHEET } from "../lib/asked.ts";
import {
  aboutOf,
  bookmarkKey,
  bookmarkName,
  bookmarkPath,
  bookmarksIn,
  isBookmarked,
  setBookmark,
} from "../lib/bookmarks.ts";
import { crumbsOf, type FolderIndex, folderHref, folderIndex, namedLike, pathText } from "../lib/folders.ts";
import { ignore } from "../lib/ignore.ts";
import { type Command, type Context, registerGroup, registerSource } from "../lib/omnibar.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { peeked } from "../lib/peek.ts";
import { placeHref, reportTitle } from "../lib/reports.ts";
import { asApiError } from "../lib/resource.ts";
import { atLeast, canEdit, canEditRules, canEditSchema } from "../lib/roles.ts";
import { location, spacePath } from "../lib/router.ts";
import { me, mySpaces } from "../lib/state.ts";
import { applyTheme, shownTheme } from "../lib/theme.ts";
import { toast } from "../lib/toast.ts";
import { OrgLogo } from "../ui/Avatar.tsx";
import { EntityGlyph } from "../ui/EntityGlyph.tsx";
import {
  ACCOUNT_ICONS,
  AgentIcon,
  BookmarkedIcon,
  BookmarkIcon,
  CatalogIcon,
  CommentIcon,
  DarkIcon,
  EntitiesIcon,
  FolderIcon,
  InviteIcon,
  IssuesIcon,
  LightIcon,
  MapIcon,
  NAV_ICONS,
  OrgIcon,
  PlusIcon,
  SETTINGS_ICONS,
  SignOutIcon,
  SlidersIcon,
} from "../ui/icons.tsx";

/** One of our icons (ui/icons.tsx). */
type Glyph = typeof PlusIcon;

/** Items found as you type, at most. */
const ITEMS_MAX = 8;
/** Reports found as you type, at most. */
const REPORTS_MAX = 5;

/** Other names a command is found by: its name in English, and its keywords in your language and in English. */
function also(label: Key, keywords?: Key): string[] {
  const words = [english(label), ...(keywords ? [t(keywords), english(keywords)] : [])];
  return [...new Set(words)];
}

/** A command of `more.group` named by `label` (and found by `more.keywords`), at `href`. */
function link(
  id: string,
  label: Key,
  href: string,
  more: { group: string; keywords?: Key; hint?: string; icon?: Command["icon"] },
): Command {
  return {
    id,
    group: more.group,
    label: t(label),
    keywords: also(label, more.keywords),
    href,
    ...(more.hint !== undefined && { hint: more.hint }),
    ...(more.icon !== undefined && { icon: more.icon }),
  };
}

/** One of the space's sections: its path, name, words to find it by, and its icon. */
type PageDef = [path: string, label: Key, keywords: Key | undefined, icon: Glyph];

const SPACE_PAGES: PageDef[] = [
  ["/triage", "nav.triage", "omnibar.kw.triage", NAV_ICONS.triage],
  ["/queue", "nav.queue", "omnibar.kw.queue", NAV_ICONS.queue],
  ["/conversation", "nav.conversation", "omnibar.kw.conversation", NAV_ICONS.conversation],
  ["/reports", "nav.reports", "omnibar.kw.reports", NAV_ICONS.reports],
  ["/items", "nav.items", "omnibar.kw.items", NAV_ICONS.items],
  ["/entities", "items.tab.entities", "omnibar.kw.entities", EntitiesIcon],
  ["/entities/map", "omnibar.page.map", "omnibar.kw.map", MapIcon],
  ["/issues", "items.tab.issues", "omnibar.kw.issues", IssuesIcon],
  ["/rules", "nav.rules", "omnibar.kw.rules", NAV_ICONS.rules],
  ["/history", "nav.history", "omnibar.kw.history", NAV_ICONS.history],
  ["/bookmarks", "nav.bookmarks", "omnibar.kw.bookmarks", NAV_ICONS.bookmarks],
  ["/trash", "nav.trash", "omnibar.kw.trash", NAV_ICONS.trash],
];

/** The settings pages: path below `/settings`, name, keywords, whether only admins see it, and whose it is. */
type SettingsDef = [path: string, label: Key, keywords: Key | undefined, admin: boolean, of: "space" | "org"];

const SETTINGS_PAGES: SettingsDef[] = [
  ["", "settings.titleSpace", "omnibar.kw.settings", false, "space"],
  ["/hooks", "settings.tab.hooks", "omnibar.kw.hooks", true, "space"],
  ["/org", "settings.titleOrg", "omnibar.kw.settings", false, "org"],
  ["/spaces", "settings.tab.spaces", undefined, false, "org"],
  ["/members", "settings.tab.members", "omnibar.kw.members", false, "org"],
  ["/billing", "settings.tab.billing", "omnibar.kw.billing", false, "org"],
  ["/usage", "settings.tab.usage", "omnibar.kw.usage", false, "org"],
  ["/keys", "settings.tab.keys", "omnibar.kw.keys", false, "org"],
  ["/privacy", "settings.tab.privacy", "omnibar.kw.privacy", true, "org"],
];

/** Your account's pages. */
const ACCOUNT_PAGES: [path: string, label: Key, keywords: Key | undefined][] = [
  ["/profile", "account.tab.profile", "omnibar.kw.profile"],
  ["/profile/orgs", "account.tab.orgs", undefined],
  ["/profile/sessions", "account.tab.sessions", "omnibar.kw.sessions"],
  ["/profile/privacy", "omnibar.page.accountPrivacy", "omnibar.kw.accountPrivacy"],
  ["/profile/device", "account.tab.device", "omnibar.kw.device"],
];

const glyph = (G: Glyph) => <G />;

/** The space's sections and settings pages (those you may open), and your account's pages. */
function pages({ space }: Context): Command[] {
  const account = ACCOUNT_PAGES.map(([path, label, keywords]) =>
    link(`page:${path}`, label, path, {
      group: "pages",
      hint: t("account.title"),
      icon: glyph(ACCOUNT_ICONS[path as keyof typeof ACCOUNT_ICONS]),
      ...(keywords && { keywords }),
    }),
  );
  if (!space) {
    return account;
  }
  const admin = atLeast(space.role, "admin");
  const sections = SPACE_PAGES.map(([path, label, keywords, icon]) =>
    link(`page:${path}`, label, spaceUrl(space, path), {
      group: "pages",
      icon: glyph(icon),
      ...(keywords && { keywords }),
    }),
  );
  const settingsPages = SETTINGS_PAGES.filter(([, , , onlyAdmins]) => admin || !onlyAdmins).map(
    ([path, label, keywords, , of]) =>
      link(`page:/settings${path}`, label, spaceUrl(space, `/settings${path}`), {
        group: "pages",
        icon: glyph(SETTINGS_ICONS[path as keyof typeof SETTINGS_ICONS] ?? NAV_ICONS.settings),
        ...(keywords && { keywords }),
        ...(path !== "" && path !== "/org" && { hint: t(of === "org" ? "settings.titleOrg" : "settings.titleSpace") }),
      }),
  );
  return [...sections, ...settingsPages, ...account];
}

/** One setting: where it is (a page's path and the setting's anchor), its name and keywords, and who sees it. */
type SettingDef = [
  page: "account" | "space" | "org",
  anchor: string,
  label: Key,
  keywords: Key | undefined,
  admin: boolean,
];

const SETTINGS: SettingDef[] = [
  ["account", "theme", "profile.theme", "omnibar.kw.theme", false],
  ["account", "appearance", "profile.appearance", "omnibar.kw.appearance", false],
  ["account", "language", "profile.language", "omnibar.kw.language", false],
  ["account", "time-zone", "profile.timeZone", "omnibar.kw.timeZone", false],
  ["account", "mentions-email", "profile.mentionsEmail", "omnibar.kw.notifications", false],
  ["account", "triage-email-addresses", "profile.triageEmailAddresses", "omnibar.kw.triageEmailAddress", false],
  ["space", "triage-email-address", "settings.triageEmailAddress", "omnibar.kw.triageEmailAddress", false],
  ["space", "time-zone", "settings.timeZone", "omnibar.kw.timeZone", false],
  ["space", "locks", "settings.locks", "omnibar.kw.locks", false],
  ["space", "data", "settings.export", "omnibar.kw.export", true],
  ["org", "allowed-senders", "settings.allowlist", "omnibar.kw.senders", false],
  ["org", "blocked-senders", "settings.blocklist", "omnibar.kw.senders", false],
  ["org", "private-items", "othersPrivate.section", undefined, true],
];

/** Settings, each a link straight to it (its page, scrolled to it). */
function settings({ space }: Context): Command[] {
  const addresses = mySpaces().some((s) => s.triage_email_address);
  return SETTINGS.flatMap(([page, anchor, label, keywords, admin]) => {
    if ((page !== "account" && !space) || (admin && !(space && atLeast(space.role, "admin")))) {
      return [];
    }
    if (anchor === "triage-email-addresses" && !addresses) {
      return [];
    }
    const base =
      page === "account" ? "/profile" : spaceUrl(space as SpaceOut, page === "org" ? "/settings/org" : "/settings");
    const hint = { account: "account.title", space: "settings.titleSpace", org: "settings.titleOrg" } as const;
    return [
      link(`setting:${page}:${anchor}`, label, `${base}#${anchor}`, {
        group: "settings",
        hint: t(hint[page]),
        icon: <SlidersIcon />,
        ...(keywords && { keywords }),
      }),
    ];
  });
}

/** Shows the theme `to` and keeps it in your profile (back to the one before if that fails). */
async function switchTheme(to: "light" | "dark"): Promise<void> {
  const u = me.value as Me;
  const before = { theme: u.theme, trueBlack: u.true_black };
  applyTheme({ theme: to, trueBlack: u.true_black });
  try {
    const saved = await api.patch<Me>("/me", { theme: to });
    me.value = { ...(me.value as Me), theme: saved.theme };
  } catch (e) {
    applyTheme(before);
    toast(asApiError(e).message, "error");
  }
}

/** A path below a space that's an item's page. */
const ITEM_PAGE = /^items\/([^/]+)$/u;

/** The item open in `space`: in a quick look (in front), else on its page; none elsewhere. */
function openItem(space: SpaceOut): string | null {
  const here = spacePath(location.value.pathname);
  const page = here?.space === space.id ? here.rest.match(ITEM_PAGE)?.[1] : undefined;
  return peeked() ?? page ?? null;
}

/** Things to do: make something, invite someone, bookmark the item open, switch the theme, sign out. */
function actions({ space }: Context): Command[] {
  const out: Command[] = [];
  const add = (label: Key, keywords: Key, href: string, icon: Command["icon"] = <PlusIcon />) =>
    out.push(link(`action:${label}`, label, href, { group: "actions", keywords, icon }));
  if (space && canEdit(space)) {
    add("items.new", "omnibar.kw.newItem", askHref(spaceUrl(space, "/items"), NEW_ITEM));
    add("documents.newDoc", "omnibar.kw.newDocument", askHref(spaceUrl(space, "/items"), NEW_DOCUMENT));
    add("documents.newSheet", "omnibar.kw.newSheet", askHref(spaceUrl(space, "/items"), NEW_SPREADSHEET));
  }
  if (space && canEditSchema(space)) {
    add("entities.new", "omnibar.kw.newEntity", spaceUrl(space, "/entities/new"));
    add("entities.catalog", "omnibar.kw.catalog", spaceUrl(space, "/entities/catalog"), <CatalogIcon />);
  }
  if (space && canEditRules(space)) {
    add("rules.new", "omnibar.kw.newRule", spaceUrl(space, "/rules/new"));
  }
  if (space && atLeast(space.role, "admin")) {
    add("members.invite", "omnibar.kw.invite", askHref(spaceUrl(space, "/settings/members"), INVITE), <InviteIcon />);
    add("members.addAgent", "omnibar.kw.agent", spaceUrl(space, "/settings/members"), <AgentIcon />);
  }
  const open = space ? openItem(space) : null;
  if (space && open) {
    const target = { kind: "item", item: open } as const;
    const on = isBookmarked(space.id, target);
    const label: Key = on ? "omnibar.action.unbookmark" : "omnibar.action.bookmark";
    out.push({
      id: "action:bookmark",
      group: "actions",
      label: t(label),
      keywords: also(label, "omnibar.kw.bookmark"),
      icon: on ? <BookmarkedIcon /> : <BookmarkIcon />,
      run: () => {
        setBookmark(space.id, target, !on).catch(ignore);
      },
    });
  }
  const dark = shownTheme() !== "paper";
  const theme: Key = dark ? "omnibar.action.light" : "omnibar.action.dark";
  out.push({
    id: "action:theme",
    group: "actions",
    label: t(theme),
    keywords: also(theme, "omnibar.kw.theme"),
    icon: dark ? <LightIcon /> : <DarkIcon />,
    run: () => {
      switchTheme(dark ? "light" : "dark").catch(ignore);
    },
  });
  out.push({
    id: "action:signOut",
    group: "actions",
    label: t("nav.signOut"),
    keywords: also("nav.signOut", "omnibar.kw.signOut"),
    icon: <SignOutIcon />,
    run: () => {
      logout().catch(ignore);
    },
  });
  return out;
}

/** The space's entities: each one's items. */
function entities({ space }: Context): Command[] {
  return (space?.entities ?? []).map((e) => ({
    id: `entity:${e.id}`,
    group: "entities",
    label: e.name,
    keywords: [t("omnibar.kw.entity"), english("omnibar.kw.entity")],
    href: `${spaceUrl(space as SpaceOut, "/items")}?entity=${e.id}`,
    icon: <EntityGlyph icon={e.icon} size={20} empty="dot" />,
  }));
}

/** Your other spaces (going to one: its triage area), named with their organization where that tells them apart. */
function spaces({ space }: Context): Command[] {
  const all = mySpaces();
  const orgs = new Set(all.map((s) => s.org.org)).size;
  return all
    .filter((s) => s.space !== space?.id)
    .map((s) => ({
      id: `space:${s.space}`,
      group: "spaces",
      label: s.name,
      keywords: [s.org.name, t("omnibar.kw.space"), english("omnibar.kw.space")],
      href: spaceUrl({ id: s.space }, "/triage"),
      icon: s.org.logo ? <OrgLogo id={s.org.org} name={s.org.name} logo={s.org.logo} size={18} /> : <OrgIcon />,
      ...(orgs > 1 || s.org.name !== s.name ? { hint: s.org.name } : {}),
    }));
}

/** The space's items whose title (or text) matches what's typed, the server's best first. */
async function items(query: string, { space }: Context, signal: AbortSignal): Promise<Command[]> {
  if (!space) {
    return [];
  }
  const rows = await api.get<ItemRow[]>(spaceApi(space, "/items/suggest"), { q: query, limit: ITEMS_MAX }, signal);
  return rows.map((r) => {
    const entity = space.entities.find((e) => e.id === r.entity);
    return {
      id: `item:${r.id}`,
      group: "items",
      label: r.title || t("common.untitled"),
      href: spaceUrl(space, `/items/${r.id}`),
      // Its icon is drawn from its look (the item list's: a PDF, an image, an email…), its entity's beside its name.
      look: {
        kind: r.kind,
        ...(r.file?.mime && { mime: r.file.mime }),
        ...(entity && { entity: entity.id }),
      },
      peek: r.id,
      remember: true,
      scope: space.id,
      ...(entity && { hint: entity.name }),
    };
  });
}

/** Folders found as you type, at most. */
const FOLDERS_MAX = 6;
/** How long the space's folders, asked once, are searched before they're asked again. */
const FOLDERS_FRESH_MS = 30_000;
/** The space's folders, as last asked (one space's: the one searched). */
let folderCache: { space: string; at: number; index: FolderIndex } | null = null;

/** The space's folders whose names have what's typed, each with where it is (its path) and to its page. */
async function folders(query: string, { space }: Context, signal: AbortSignal): Promise<Command[]> {
  if (!space) {
    return [];
  }
  if (!folderCache || folderCache.space !== space.id || Date.now() - folderCache.at > FOLDERS_FRESH_MS) {
    folderCache = { space: space.id, at: Date.now(), index: await folderIndex(space, signal) };
  }
  const { index } = folderCache;
  return namedLike(index, query, null)
    .slice(0, FOLDERS_MAX)
    .map((f) => {
      const above = crumbsOf(index, f.id).slice(0, -1);
      return {
        id: `folder:${f.id}`,
        group: "folders",
        label: f.name,
        href: folderHref(space, f.id),
        icon: <FolderIcon />,
        ...(above.length > 0 && { hint: pathText(above) }),
      };
    });
}

/** The space's reports whose title or label has what's typed, newest first: each to its place in its conversation. */
async function reports(query: string, { space }: Context, signal: AbortSignal): Promise<Command[]> {
  if (!space) {
    return [];
  }
  const list = await api.get<ReportList>(spaceApi(space, "/reports"), { q: query, limit: REPORTS_MAX }, signal);
  const Icon = NAV_ICONS.reports;
  return list.data.map((c) => ({
    id: `report:${c.id}`,
    group: "reports",
    label: reportTitle(c),
    keywords: [...(c.label ? [c.label] : []), t("omnibar.kw.report"), english("omnibar.kw.report")],
    hint: [c.label, c.subject_title ?? t("reports.inChannel")].filter(Boolean).join(" · "),
    href: placeHref(space, c),
    icon: <Icon />,
  }));
}

/** Each kind of bookmark's icon (an item's is drawn from its look, when it's known; a kind this app doesn't know yet: a bookmark's). */
const BOOKMARK_ICONS: Record<string, Glyph> = {
  item: NAV_ICONS.items,
  conversation: NAV_ICONS.conversation,
  comment: CommentIcon,
  commit: NAV_ICONS.history,
};

/** A comment's bookmark, in words: who wrote it and how it starts (found by those); where it is, beside. */
const commentWords = (b: Bookmark): { label: string; hint: string } => ({
  label: `${b.author_name ?? ""}: ${b.excerpt ?? t("bookmarks.noText")}`,
  hint: aboutOf(b) === "space" ? t("bookmarks.channel") : bookmarkName(b),
});

/** Your bookmarks in the space (those still there), found by their titles. */
function bookmarks({ space }: Context): Command[] {
  if (!space) {
    return [];
  }
  return bookmarksIn(space.id)
    .filter((b) => b.available)
    .map((b) => {
      const Icon = BOOKMARK_ICONS[b.kind] ?? BookmarkIcon;
      const about = aboutOf(b);
      const comment = b.kind === "comment" ? commentWords(b) : undefined;
      return {
        id: `bookmark:${bookmarkKey(b)}`,
        group: "bookmarks",
        label: comment?.label ?? bookmarkName(b),
        keywords: [t("nav.bookmarks"), english("nav.bookmarks")],
        href: spaceUrl(space, bookmarkPath(b)),
        icon: <Icon />,
        ...(b.kind === "item" && b.item ? { peek: b.item } : {}),
        ...(b.kind === "conversation" && about !== "space" ? { hint: t(`bookmarks.about.${about}`) } : {}),
        ...(comment ? { hint: comment.hint } : {}),
      };
    });
}

/** The omnibar's own sections and sources (once). */
let registered = false;
export function registerBuiltins(): void {
  if (registered) {
    return;
  }
  registered = true;
  const groups: [string, Key, boolean?][] = [
    ["recent", "omnibar.group.recent"],
    ["pages", "omnibar.group.pages"],
    ["actions", "omnibar.group.actions"],
    ["settings", "omnibar.group.settings"],
    ["entities", "omnibar.group.entities"],
    ["bookmarks", "nav.bookmarks"],
    ["spaces", "omnibar.group.spaces"],
    ["folders", "omnibar.group.folders", true],
    ["items", "omnibar.group.items", true],
    ["reports", "omnibar.group.reports", true],
  ];
  for (const [i, [id, title, searched]] of groups.entries()) {
    registerGroup({ id, title: () => t(title), order: i * 10, ...(searched && { searched }) });
  }
  registerSource({ id: "pages", commands: pages });
  registerSource({ id: "actions", commands: actions });
  registerSource({ id: "settings", commands: settings });
  registerSource({ id: "entities", commands: entities });
  registerSource({ id: "bookmarks", commands: bookmarks });
  registerSource({ id: "spaces", commands: spaces });
  registerSource({ id: "folders", search: folders });
  registerSource({ id: "items", search: items });
  registerSource({ id: "reports", search: reports });
}
